// District + building-style content, verbatim from v1 js/constants.js:2-309.
// v1 had 14 keys where prenzlauer2-4 / zehlendorf2-4 were near-clones; here
// they are 7 DistrictDefs plus a variant SEQUENCE with overrides (the plan's
// Phase 7 data model, built correctly from the start).

export type Spacing = 'sparse' | 'medium' | 'dense' | 'packed';
export type BuildingType = 'apartment' | 'office' | 'mixed' | 'industrial' | 'family' | 'villa';
export type StyleName =
  'gründerzeit' | 'modernist' | 'postwar' | 'contemporary' | 'villa' | 'industrial';

export interface BuildingConfig {
  heightRange: readonly [number, number];
  types: readonly BuildingType[];
  spacing: Spacing;
  predominantStyles: readonly StyleName[];
  gardenChance: number;
  courtyardChance: number;
  tallBuildingChance?: number;
}

export interface DistrictDef {
  id: string;
  name: string;
  dojoName: string;
  environment: 'urban-dense' | 'industrial' | 'residential' | 'urban' | 'suburban';
  difficulty: 'easy' | 'medium' | 'hard';
  buildingDensity: number;
  trafficDensity: number;
  greenery: number;
  baseSpeed: number;
  carSpeedMultiplier: number;
  buildings: BuildingConfig;
}

export const DISTRICTS: Record<string, DistrictDef> = {
  mitte: {
    id: 'mitte',
    name: 'Mitte',
    dojoName: 'Samurai Karate Mitte',
    environment: 'urban-dense',
    difficulty: 'hard',
    buildingDensity: 1.5,
    trafficDensity: 1.2,
    greenery: 0.1,
    baseSpeed: 0.12,
    carSpeedMultiplier: 1.0,
    buildings: {
      heightRange: [4, 7],
      types: ['apartment', 'office', 'mixed'],
      spacing: 'packed',
      predominantStyles: ['gründerzeit', 'modernist', 'contemporary'],
      gardenChance: 0.0,
      courtyardChance: 0.3,
    },
  },
  kreuzberg: {
    id: 'kreuzberg',
    name: 'Kreuzberg',
    dojoName: 'Samurai Karate Kreuzberg',
    environment: 'industrial',
    difficulty: 'hard',
    buildingDensity: 1.3,
    trafficDensity: 1.1,
    greenery: 0.2,
    baseSpeed: 0.11,
    carSpeedMultiplier: 1.2,
    buildings: {
      heightRange: [3, 6],
      types: ['apartment', 'industrial', 'mixed'],
      spacing: 'packed',
      predominantStyles: ['postwar', 'industrial', 'gründerzeit'],
      gardenChance: 0.1,
      courtyardChance: 0.2,
    },
  },
  prenzlauer: {
    id: 'prenzlauer',
    name: 'Prenzlauer Berg',
    dojoName: 'Ki-Dojo e.V.',
    environment: 'residential',
    difficulty: 'medium',
    buildingDensity: 1.2,
    trafficDensity: 0.8,
    greenery: 0.4,
    baseSpeed: 0.1,
    carSpeedMultiplier: 1.3,
    buildings: {
      heightRange: [3, 5],
      types: ['apartment', 'mixed'],
      spacing: 'packed',
      predominantStyles: ['gründerzeit', 'modernist'],
      gardenChance: 0.3,
      courtyardChance: 0.4,
    },
  },
  schoeneberg: {
    id: 'schoeneberg',
    name: 'Schöneberg',
    dojoName: 'Samurai Karate Berlin',
    environment: 'urban',
    difficulty: 'medium',
    buildingDensity: 1.3,
    trafficDensity: 0.9,
    greenery: 0.3,
    baseSpeed: 0.1,
    carSpeedMultiplier: 1.2,
    buildings: {
      heightRange: [3, 6],
      types: ['apartment', 'mixed'],
      spacing: 'packed',
      predominantStyles: ['gründerzeit', 'modernist', 'postwar'],
      gardenChance: 0.2,
      courtyardChance: 0.3,
    },
  },
  pankow: {
    id: 'pankow',
    name: 'Pankow',
    dojoName: 'Ki-Dojo e.V.',
    environment: 'residential',
    difficulty: 'medium',
    buildingDensity: 0.6,
    trafficDensity: 0.7,
    greenery: 0.5,
    baseSpeed: 0.1,
    carSpeedMultiplier: 1.8,
    buildings: {
      heightRange: [1, 2],
      types: ['apartment', 'family'],
      spacing: 'medium',
      predominantStyles: ['postwar', 'contemporary'],
      gardenChance: 0.4,
      courtyardChance: 0.2,
      tallBuildingChance: 0.05,
    },
  },
  frohnau: {
    id: 'frohnau',
    name: 'Frohnau/Glienicke',
    dojoName: 'Samurai Karate Frohnau',
    environment: 'suburban',
    difficulty: 'easy',
    buildingDensity: 0.3,
    trafficDensity: 0.4,
    greenery: 0.9,
    baseSpeed: 0.09,
    carSpeedMultiplier: 2.5,
    buildings: {
      heightRange: [1, 2],
      types: ['family', 'villa'],
      spacing: 'sparse',
      predominantStyles: ['villa', 'contemporary'],
      gardenChance: 0.9,
      courtyardChance: 0.1,
      tallBuildingChance: 0.02,
    },
  },
  zehlendorf: {
    id: 'zehlendorf',
    name: 'Zehlendorf',
    dojoName: 'Ki-Dojo e.V.',
    environment: 'suburban',
    difficulty: 'easy',
    buildingDensity: 0.4,
    trafficDensity: 0.5,
    greenery: 0.8,
    baseSpeed: 0.09,
    carSpeedMultiplier: 2.3,
    buildings: {
      heightRange: [1, 2],
      types: ['family', 'villa'],
      spacing: 'sparse',
      predominantStyles: ['villa', 'contemporary'],
      gardenChance: 0.8,
      courtyardChance: 0.1,
      tallBuildingChance: 0.03,
    },
  },
};

/** v1's 14-entry roster as variant overrides on the 7 real districts.
 *  Overrides carry only the fields that differed in v1 (js/constants.js:61-257). */
export interface DistrictVariant {
  district: string;
  overrides?: Partial<
    Pick<DistrictDef, 'buildingDensity' | 'trafficDensity' | 'greenery' | 'carSpeedMultiplier'>
  > & { gardenChance?: number; tallBuildingChance?: number };
}

export const SEQUENCE: readonly DistrictVariant[] = [
  { district: 'mitte' },
  { district: 'kreuzberg' },
  { district: 'prenzlauer' }, // prenzlauer
  { district: 'prenzlauer' }, // prenzlauer2 (identical in v1)
  { district: 'prenzlauer', overrides: { greenery: 0.5, gardenChance: 0.4 } }, // prenzlauer3
  {
    district: 'prenzlauer',
    overrides: { buildingDensity: 1.1, trafficDensity: 0.7, carSpeedMultiplier: 1.4 },
  }, // prenzlauer4
  { district: 'schoeneberg' },
  { district: 'pankow' },
  { district: 'frohnau' },
  { district: 'zehlendorf' }, // zehlendorf
  { district: 'zehlendorf', overrides: { greenery: 0.7 } }, // zehlendorf2
  { district: 'zehlendorf' }, // zehlendorf3 (identical in v1)
  {
    district: 'zehlendorf',
    overrides: {
      buildingDensity: 0.3,
      trafficDensity: 0.4,
      greenery: 0.9,
      carSpeedMultiplier: 2.4,
      gardenChance: 0.9,
      tallBuildingChance: 0.02,
    },
  }, // zehlendorf4
];

export function resolveVariant(variant: DistrictVariant): DistrictDef {
  const base = DISTRICTS[variant.district];
  if (!base) throw new Error(`Unknown district: ${variant.district}`);
  if (!variant.overrides) return base;
  const { gardenChance, tallBuildingChance, ...rest } = variant.overrides;
  const buildings: BuildingConfig = {
    ...base.buildings,
    ...(gardenChance !== undefined ? { gardenChance } : {}),
    ...(tallBuildingChance !== undefined ? { tallBuildingChance } : {}),
  };
  return { ...base, ...rest, buildings };
}

// v1 js/constants.js:266-309 (BUILDING_STYLES)
export interface BuildingStyle {
  colors: readonly number[];
  roofColor: number;
  ornaments: boolean;
  balconyChance: number;
}

export const BUILDING_STYLES: Record<StyleName, BuildingStyle> = {
  gründerzeit: {
    colors: [0x8b4513, 0xa0522d, 0x654321],
    roofColor: 0x2f4f4f,
    ornaments: true,
    balconyChance: 0.3,
  },
  modernist: {
    colors: [0xf5f5dc, 0xffffff, 0xf0f8ff],
    roofColor: 0x696969,
    ornaments: false,
    balconyChance: 0.2,
  },
  postwar: {
    colors: [0x708090, 0x778899, 0x696969],
    roofColor: 0x2f4f4f,
    ornaments: false,
    balconyChance: 0.4,
  },
  contemporary: {
    colors: [0xc0c0c0, 0xb0b0b0, 0xd3d3d3],
    roofColor: 0x000000,
    ornaments: false,
    balconyChance: 0.1,
  },
  villa: {
    colors: [0xf5f5dc, 0xf0f8ff, 0xffffff, 0xdddddd],
    roofColor: 0x8b4513,
    ornaments: true,
    balconyChance: 0.1,
  },
  industrial: {
    colors: [0x696969, 0x708090, 0x2f4f4f],
    roofColor: 0x000000,
    ornaments: false,
    balconyChance: 0.0,
  },
};

// v1 js/constants.js:369-375 (DOJO_WEBSITES)
export const DOJO_WEBSITES: Record<string, string> = {
  'Samurai Karate Mitte': 'https://samurai-karate.berlin',
  'Samurai Karate Kreuzberg': 'https://www.karate-kreuzberg.de',
  'Ki-Dojo e.V.': 'https://ki-dojo.de',
  'Samurai Karate Berlin': 'https://samurai-karate.berlin',
  'Samurai Karate Frohnau': 'https://www.karate-frohnau.de',
};

// v1 js/constants.js:344-357 (COLORS.SKY / COLORS.GROUND)
export const SKY_COLORS = {
  night: 0x1a1a2e,
  dawn: 0xb8859a,
  day: 0x87ceeb,
  dusk: 0xd4824a,
} as const;

export const GROUND_COLORS = {
  street: 0x404040,
  pavement: 0xcccccc,
  grass: 0x228b22,
  concrete: 0x888888,
} as const;
